import React, { useCallback, useContext, useMemo, useState } from "react";
import { useFragment } from "react-relay";

import { faVideo } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { isPast, isToday } from "date-fns";
import { memoize, noop } from "lodash";
import { graphql } from "relay-runtime";

import { DismissibleElementsContext } from "~/common/DismissibleElements";
import { TeamsContext } from "~/integrations/teams/teamsContext";
import chromeExtensionPreview from "~/legacy-assets/images/extensions/chromeMeetExtension.png";
import firefoxExtensionPreview from "~/legacy-assets/images/extensions/firefoxMeetExtension.png";
import zoomExtensionPreview from "~/legacy-assets/images/extensions/zoomExtension.png";
import { ReactComponent as GoogleMeetIcon } from "~/legacy-assets/images/logos/google_meet.svg";
import { ReactComponent as GoogleMeetWord } from "~/legacy-assets/images/logos/google_meet_word.svg";
import { ReactComponent as TeamsIcon } from "~/legacy-assets/images/logos/ms_teams.svg";
import { ReactComponent as WebexIcon } from "~/legacy-assets/images/logos/webex.svg";
import { ReactComponent as ZoomIcon } from "~/legacy-assets/images/logos/zoom_icon_only.svg";
import { ReactComponent as ZoomWordIcon } from "~/legacy-assets/images/logos/zoom_word.svg";
import {
	chromeStoreLink,
	inVideocallMode,
	isChrome,
	isFirefox,
	isTeams,
	isZoom,
	mozillaStoreLink,
	withQueryParams,
	zoomStoreLink,
} from "~/lib/utils";
import { ZOOM_APP_UPDATE_FEATURE_FLAG } from "~/settings/Integrations/featureFlag";
import { AnchorButton, Button, Checkbox } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import MeetingPhaseContext from "../MeetingPhaseContext";

import type {
	ConferenceCallProvider,
	JoinConferenceButton_event$data as EVENT,
	JoinConferenceButton_event$key as EventKey,
} from "~/__generated__/JoinConferenceButton_event.graphql";
import type { JoinConferenceButton_viewer$key as ViewerKey } from "~/__generated__/JoinConferenceButton_viewer.graphql";

import styles from "./JoinConferenceButton.module.css";
import noteHeaderStyles from "./NoteHeader.module.css";

const viewerFragment = graphql`
	fragment JoinConferenceButton_viewer on Viewer {
		uxSimplificationEnabled
		lastUsedBrowserExtension
		integrations {
			zoom {
				isConnected
				hasLocalRecordingTokenScope
			}
		}
	}
`;

const eventFragment = graphql`
	fragment JoinConferenceButton_event on Event {
		guid
		conferenceCalls {
			provider
			url
		}
	}
`;

export const getCallName = (provider: ConferenceCallProvider) => {
	switch (provider) {
		case "MS_TEAMS":
			return "Teams Meeting";
		case "ZOOM":
			return "Zoom Conference";
		case "HANGOUTS_MEET":
			return "Google Meet";
		case "SKYPE_FOR_BUSINESS":
			return "Skype Call";
		case "WEBEX":
			return "Webex Meeting";
		default:
			return "Conference";
	}
};

const CALL_ORDER_IN_MENU: { [key in ConferenceCallProvider]: number } = {
	HANGOUTS_MEET: 0,
	MS_TEAMS: 0,
	ZOOM: 0,
	SKYPE_FOR_BUSINESS: 1,
	WEBEX: 1,
	UNKNOWN: 2,
};

export function CallIcon({ provider, className }: { provider: ConferenceCallProvider; className?: string }) {
	return (
		<>
			{provider === "ZOOM" ? (
				<ZoomIcon className={className} />
			) : provider === "HANGOUTS_MEET" ? (
				<GoogleMeetIcon className={className} />
			) : provider === "MS_TEAMS" ? (
				<TeamsIcon className={className} />
			) : provider === "WEBEX" ? (
				<WebexIcon className={className} />
			) : (
				<FontAwesomeIcon icon={faVideo} className={className} />
			)}
		</>
	);
}

export type CALL = EVENT["conferenceCalls"][number];
export function maybeOpenCompanionMode(eventGuid: string, call: CALL) {
	const disableCompanionModeForTheseProviders =
		window?.electron?.settingsAPI?.get("disableCompanionModeForTheseProviders") ?? [];
	const useCompanionFromNoteHeader = window?.electron?.settingsAPI?.get("useCompanionFromNoteHeader") ?? false;
	if (useCompanionFromNoteHeader && !disableCompanionModeForTheseProviders.includes(call.provider)) {
		window.electron?.navigateCompanionMode?.(`/companion-mode/event/${eventGuid}/`);
	}
}

interface JoinButtonProps {
	call: CALL;
	openCall: (e: React.MouseEvent) => void;
	uxSimplificationEnabled: boolean;
}

function JoinButton({ call: { url, provider }, openCall, uxSimplificationEnabled }: JoinButtonProps) {
	const buttonText = uxSimplificationEnabled ? getCallName(provider) : "Join";

	return (
		<AnchorButton
			size="slim"
			variant="secondary"
			target="_blank"
			rel="noopener noreferrer"
			href={url}
			onClick={openCall}
			className={cx(noteHeaderStyles.ultraSlimButton, styles.joinButton, {
				[styles.oldFont]: !uxSimplificationEnabled,
			})}
		>
			<CallIcon provider={provider} className={styles.providerIconInButton} />
			<span className={styles.hideIfNoSpace}>&ensp;{buttonText}</span>
		</AnchorButton>
	);
}

interface JoinMenuProps {
	calls: readonly CALL[];
	openCall: (call: CALL) => (e: React.MouseEvent) => void;
	menuIsOpen: boolean;
	setMenuIsOpen: (value: boolean) => void;
	uxSimplificationEnabled: boolean;
}

function JoinMenu({ calls, openCall, menuIsOpen, setMenuIsOpen, uxSimplificationEnabled }: JoinMenuProps) {
	const sortedCalls = useMemo(() => {
		return Array.from(calls).sort(
			(callA, callB) => CALL_ORDER_IN_MENU[callA.provider] - CALL_ORDER_IN_MENU[callB.provider],
		);
	}, [calls]);

	return (
		<Menu open={menuIsOpen} onOpenChange={setMenuIsOpen}>
			<Menu.Trigger>
				<Button
					size="slim"
					variant="secondary"
					className={cx(noteHeaderStyles.ultraSlimButton, styles.joinButton, {
						[styles.oldFont]: !uxSimplificationEnabled,
					})}
				>
					<FontAwesomeIcon icon={faVideo} className={styles.providerIconInButton} />
					&ensp;<span className={styles.hideIfNoSpace}>Join&ensp;</span>
					<FontAwesomeIcon icon={menuIsOpen ? faCaretUp : faCaretDown} />
				</Button>
			</Menu.Trigger>
			<Menu.Content>
				{sortedCalls.map(call => (
					<Menu.ItemLink
						key={call.url}
						target="_blank"
						rel="noopener noreferrer"
						href={call.url}
						onClick={openCall(call)}
					>
						<span className={styles.conferenceItem}>
							<CallIcon provider={call.provider} />
							{!uxSimplificationEnabled && "Join "}
							{getCallName(call.provider)}
						</span>
					</Menu.ItemLink>
				))}
			</Menu.Content>
		</Menu>
	);
}

type Props = {
	event: EventKey;
	viewer: ViewerKey;
};

const GOOGLE_MEET_KEY = "VideoCallButton__googleMeet";
const ZOOM_KEY_NEW_SCOPE = "VideoCallButton__zoom__newScope";

export default function JoinConferenceButton(props: Props) {
	const [modalOpen, setModalOpen] = useState(false);
	const [modalOpenedBefore, setModalOpenedBefore] = useState(false);
	const [checked, setChecked] = useState(false);
	const [menuIsOpen, setMenuIsOpen] = useState(false);
	const { hasDismissed, dismiss } = useContext(DismissibleElementsContext);
	const teamsContext = useContext(TeamsContext);
	const {
		event: { end: eventEnd },
	} = useContext(MeetingPhaseContext);

	const viewer = useFragment(viewerFragment, props.viewer);
	const event = useFragment(eventFragment, props.event);

	const { conferenceCalls } = event;

	const meetCall = conferenceCalls.find(call => call.provider === "HANGOUTS_MEET");
	const zoomCall = conferenceCalls.find(call => call.provider === "ZOOM");

	let showExtensionPrompts =
		!viewer.lastUsedBrowserExtension && !hasDismissed(GOOGLE_MEET_KEY) && meetCall && (isChrome || isFirefox);

	const canShowZoomJoin = zoomCall && !isZoom;
	const zoomAppUpdateFlagIsOn = useFeatureIsOn(ZOOM_APP_UPDATE_FEATURE_FLAG);
	const shouldUpdateScopes = zoomAppUpdateFlagIsOn && !viewer.integrations.zoom?.hasLocalRecordingTokenScope;
	const userHasZoomConnected = viewer.integrations.zoom?.isConnected === true;

	// show prompt if user is connected with zoom and needs to update their scopes, only if they haven't dismissed the prompt
	// to connect with zoom yet
	let showZoomPrompts =
		canShowZoomJoin && !hasDismissed(ZOOM_KEY_NEW_SCOPE) && (userHasZoomConnected ? shouldUpdateScopes : true);

	const openCall = useMemo(
		() =>
			memoize((showModal?: boolean) => (call: CALL) => (e: React.MouseEvent) => {
				if (showModal) {
					if (
						(call.provider === "HANGOUTS_MEET" && !hasDismissed(GOOGLE_MEET_KEY) && !showZoomPrompts) ||
						(call.provider === "ZOOM" && !hasDismissed(ZOOM_KEY_NEW_SCOPE))
					) {
						setModalOpen(true);
						setModalOpenedBefore(true);
						setMenuIsOpen(false);
						e.preventDefault();
						return;
					}
				}

				if (isTeams && call.provider === "MS_TEAMS" && teamsContext) {
					teamsContext.executeDeepLink(call.url);
					e.preventDefault();
					return;
				}

				const isModifierActive = ["Alt", "AltGraph", "Control", "Hyper", "Meta", "OS", "Shift", "Super"].some(key =>
					e.getModifierState(key),
				);

				if (isModifierActive) return;

				e.preventDefault();

				const w = window.screen.availWidth / 2;
				const h = window.screen.availHeight / 2;

				const windowFeatures = `width=${w},height=${h}`;

				maybeOpenCompanionMode(event.guid, call);
				window.open(call.url, "_blank", windowFeatures);
			}),
		[hasDismissed, event.guid, showZoomPrompts, teamsContext],
	);

	const openExtensionStore = useCallback(() => {
		setModalOpen(false);
		dismiss(GOOGLE_MEET_KEY);

		/* eslint-disable camelcase */
		if (isFirefox)
			window.open(withQueryParams(mozillaStoreLink, { utm_content: "video_call_button" }), "_blank", "noopener");
		else window.open(withQueryParams(chromeStoreLink, { utm_content: "video_call_button" }), "_blank", "noopener");
		/* eslint-enable camelcase */
	}, [dismiss]);

	const openZoomMarketPlace = useCallback(() => {
		setModalOpen(false);
		dismiss(ZOOM_KEY_NEW_SCOPE);
		/* eslint-disable-next-line camelcase */
		window.open(withQueryParams(zoomStoreLink, { utm_content: "video_call_button" }), "_blank", "noopener");
	}, [dismiss]);

	const updateZoomScopes = useCallback(() => {
		setModalOpen(false);
		window.open(`${window.location.origin}/zoom/oauth`, "_blank", "noopener");
	}, []);

	const onMeetModalClose = useCallback(() => {
		setModalOpen(false);
		if (checked) dismiss(GOOGLE_MEET_KEY);
	}, [checked, dismiss]);

	const onZoomModalClose = useCallback(() => {
		setModalOpen(false);
		if (checked) dismiss(ZOOM_KEY_NEW_SCOPE);
	}, [checked, dismiss]);

	const onJoinFromModal = (call: CALL) => () => {
		if (call.provider === "HANGOUTS_MEET") {
			showExtensionPrompts = false;
			onMeetModalClose();
		}
		if (call.provider === "ZOOM") {
			showZoomPrompts = false;
			onZoomModalClose();
		}

		const w = window.screen.availWidth / 2;
		const h = window.screen.availHeight / 2;

		const windowFeatures = `width=${w},height=${h}`;

		maybeOpenCompanionMode(event.guid, call);
		window.open(call.url, "_blank", windowFeatures);
	};

	const onCheckboxChange = useCallback(() => {
		setChecked(checked => !checked);
	}, []);

	const meetModalContent = (
		<Modal isOpen={modalOpen} onClose={onMeetModalClose} width="42.25rem">
			<Modal.Body className={styles.modalContent}>
				<GoogleMeetWord className={styles.meetLogo} />
				<div className={styles.modalText}>
					Our users love having meeting agendas side-by-side in Google Meet using Fellow&apos;s{" "}
					{isFirefox ? "Firefox" : "Chrome"} extension.
				</div>
				<img className={styles.extensionPreview} src={isFirefox ? firefoxExtensionPreview : chromeExtensionPreview} />
				<div className={styles.subText}>
					Adding the extension takes less than 30 seconds. You can remove it at any time.
				</div>
			</Modal.Body>
			<Modal.Footer>
				{modalOpenedBefore && (
					<Modal.FooterSecondaryActions className={styles.footerSecondaryActions}>
						<label>
							<Checkbox checked={checked} onCheckedChange={onCheckboxChange} />
							&ensp;Don&rsquo;t show me this again
						</label>
					</Modal.FooterSecondaryActions>
				)}
				<Button size="normal" onClick={meetCall ? onJoinFromModal(meetCall) : noop}>
					Join Google Meet
				</Button>
				<Button onClick={openExtensionStore} size="normal" variant="primary">
					Add extension
				</Button>
			</Modal.Footer>
		</Modal>
	);

	const zoomModalContent = (
		<Modal isOpen={modalOpen} onClose={onZoomModalClose} width="42.25rem">
			<Modal.Body className={styles.modalContent}>
				<ZoomWordIcon className={styles.zoomLogo} />
				<div className={styles.modalText}>
					{shouldUpdateScopes
						? "Fellow's Zoom App just got better! Update it to take advantage of the improved recording features."
						: `Use Fellow's Zoom App for seamless access to meeting notes right in your calls, and enhanced recording
					features.`}
				</div>
				<img className={styles.extensionPreview} src={zoomExtensionPreview} />
			</Modal.Body>
			<Modal.Footer>
				{modalOpenedBefore && (
					<Modal.FooterSecondaryActions className={styles.footerSecondaryActions}>
						<label>
							<Checkbox checked={checked} onCheckedChange={onCheckboxChange} />
							&ensp;Don&rsquo;t ask me again
						</label>
					</Modal.FooterSecondaryActions>
				)}
				<Button size="normal" onClick={zoomCall ? onJoinFromModal(zoomCall) : noop}>
					Join Zoom
				</Button>
				<Button
					size="normal"
					onClick={shouldUpdateScopes ? updateZoomScopes : openZoomMarketPlace}
					className={styles.extensionButton}
					variant="primary"
				>
					{shouldUpdateScopes ? "Update Zoom App" : "Add Zoom App"}
				</Button>
			</Modal.Footer>
		</Modal>
	);

	// Don't show the button if the meeting is in the past
	if (isPast(eventEnd) && !isToday(eventEnd)) return null;

	// Don't show the button if we're in a call
	if (inVideocallMode) return null;

	// No calls
	if (conferenceCalls.length === 0) return null;

	// Multiple calls
	if (conferenceCalls.length > 1) {
		if (showExtensionPrompts || showZoomPrompts) {
			return (
				<>
					{showZoomPrompts ? zoomModalContent : meetModalContent}
					<JoinMenu
						calls={conferenceCalls}
						openCall={openCall(true)}
						menuIsOpen={menuIsOpen}
						setMenuIsOpen={setMenuIsOpen}
						uxSimplificationEnabled={viewer.uxSimplificationEnabled}
					/>
				</>
			);
		} else {
			return (
				<JoinMenu
					calls={conferenceCalls}
					openCall={openCall()}
					menuIsOpen={menuIsOpen}
					setMenuIsOpen={setMenuIsOpen}
					uxSimplificationEnabled={viewer.uxSimplificationEnabled}
				/>
			);
		}
	}

	// Only one call
	const call = conferenceCalls[0];
	if (showExtensionPrompts || showZoomPrompts) {
		return (
			<>
				{call.provider === "ZOOM" ? zoomModalContent : meetModalContent}
				<JoinButton
					call={call}
					openCall={openCall(true)(call)}
					uxSimplificationEnabled={viewer.uxSimplificationEnabled}
				/>
			</>
		);
	} else {
		return (
			<JoinButton call={call} openCall={openCall()(call)} uxSimplificationEnabled={viewer.uxSimplificationEnabled} />
		);
	}
}
